import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import './AdminPages.css';

const SuccessStoriesManager = () => {
  const navigate = useNavigate();
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingStory, setEditingStory] = useState(null);

  useEffect(() => {
    fetchStories();
  }, []);

  const fetchStories = async () => {
    try {
      const response = await api.get('/admin/success-stories');
      setStories(response.data || []);
    } catch (err) {
      setError('Failed to load success stories');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (storyData) => {
    try {
      await api.post('/admin/success-stories', storyData);
      setSuccess('Success story created!');
      setShowForm(false);
      fetchStories();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to create success story');
    }
  };

  const handleUpdate = async (id, storyData) => {
    try {
      await api.put(`/admin/success-stories/${id}`, storyData);
      setSuccess('Success story updated!');
      setEditingStory(null);
      fetchStories();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to update success story');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this success story?')) return;

    try {
      await api.delete(`/admin/success-stories/${id}`);
      setSuccess('Success story deleted!');
      fetchStories();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to delete success story');
    }
  };

  const handlePublish = async (id) => {
    try {
      await api.post(`/admin/success-stories/${id}/publish`);
      setSuccess('Success story published!');
      fetchStories();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to publish success story');
    }
  };

  if (loading) return <div className="loading">Loading...</div>;

  return (
    <div className="admin-page">
      <div className="container">
        <div className="page-header">
          <h1>Success Stories Manager</h1>
          <div className="button-group">
            <button onClick={() => navigate('/admin')} className="btn btn-secondary">
              Back to Dashboard
            </button>
            <button onClick={() => setShowForm(true)} className="btn btn-primary">
              + Add Success Story
            </button>
          </div>
        </div>

        {success && <div className="success">{success}</div>}
        {error && <div className="error">{error}</div>}

        {stories.length === 0 ? (
          <div className="card">
            <p style={{ textAlign: 'center', color: 'var(--secondary-color)', padding: '40px' }}>
              No success stories yet. Create your first one!
            </p>
          </div>
        ) : (
          <div className="admin-table">
            <table>
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Client</th>
                  <th>Order</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {stories.map((story) => (
                  <tr key={story.id}>
                    <td>
                      <strong>{story.title}</strong>
                      <br />
                      <small style={{ color: 'var(--secondary-color)' }}>
                        {story.description.substring(0, 100)}...
                      </small>
                    </td>
                    <td>{story.client_name || 'N/A'}</td>
                    <td>{story.order_index}</td>
                    <td>
                      <span className={`status-badge ${story.is_published ? 'status-published' : 'status-draft'}`}>
                        {story.is_published ? 'Published' : 'Draft'}
                      </span>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button onClick={() => setEditingStory(story)} className="btn btn-secondary">
                          Edit
                        </button>
                        {!story.is_published && (
                          <button onClick={() => handlePublish(story.id)} className="btn btn-success">
                            Publish
                          </button>
                        )}
                        <button onClick={() => handleDelete(story.id)} className="btn btn-danger">
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Add/Edit Form Modal */}
        {(showForm || editingStory) && (
          <div className="modal-overlay" onClick={() => { setShowForm(false); setEditingStory(null); }}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <h2>{editingStory ? 'Edit Success Story' : 'Add Success Story'}</h2>
              <SuccessStoryForm
                story={editingStory}
                onSave={editingStory ? (data) => handleUpdate(editingStory.id, data) : handleCreate}
                onCancel={() => { setShowForm(false); setEditingStory(null); }}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const SuccessStoryForm = ({ story, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    title: story?.title || '',
    description: story?.description || '',
    client_name: story?.client_name || '',
    image_url: story?.image_url || '',
    metrics: story?.metrics || '{"revenue_increase": "150%", "time_saved": "40 hours"}',
    order_index: story?.order_index || 0,
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="form-group">
        <label>Title *</label>
        <input
          type="text"
          value={formData.title}
          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
          required
        />
      </div>

      <div className="form-group">
        <label>Description *</label>
        <textarea
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          rows="4"
          required
        />
      </div>

      <div className="form-group">
        <label>Client Name</label>
        <input
          type="text"
          value={formData.client_name}
          onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
        />
      </div>

      <div className="form-group">
        <label>Image URL</label>
        <input
          type="text"
          value={formData.image_url}
          onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
          placeholder="/uploads/image.jpg"
        />
        <small style={{ color: 'var(--secondary-color)' }}>
          Upload image in Media Library and paste URL here
        </small>
      </div>

      <div className="form-group">
        <label>Metrics (JSON format)</label>
        <textarea
          value={formData.metrics}
          onChange={(e) => setFormData({ ...formData, metrics: e.target.value })}
          rows="3"
          placeholder='{"revenue_increase": "150%", "time_saved": "40 hours"}'
        />
        <small style={{ color: 'var(--secondary-color)' }}>
          Format: &#123;"metric_name": "value"&#125;
        </small>
      </div>

      <div className="form-group">
        <label>Order Index</label>
        <input
          type="number"
          value={formData.order_index}
          onChange={(e) => setFormData({ ...formData, order_index: parseInt(e.target.value) })}
          min="0"
        />
      </div>

      <div className="button-group">
        <button type="submit" className="btn btn-primary">
          Save
        </button>
        <button type="button" onClick={onCancel} className="btn btn-secondary">
          Cancel
        </button>
      </div>
    </form>
  );
};

export default SuccessStoriesManager;
